export const targetConfig = [
  { name: 'Web 网页版', value: 'web' },
  { name: 'Electron 桌面版', value: 'electron' },
]

/**
 * 渠道列表。`region` 决定 CDN / 固件 / 桌面更新源：
 * - `cn`：国内
 * - `global`：海外（含 global、global_aesco；新增海外渠道只需设 region: 'global'）
 */
export const channelConfig = [
  { name: '国内网吧', value: 'china_cybercafe', region: 'cn' },
  { name: '国内游戏零售', value: 'china_game', region: 'cn' },
  { name: '国内办公零售', value: 'china_office', region: 'cn' },
  { name: '全球海外', value: 'global', region: 'global' },
  { name: '海外Aesco', value: 'global_aesco', region: 'global' },
  { name: '国内Aesco', value: 'china_aesco', region: 'cn' },
]

/** 构建类型与业务渠道正交：同一业务渠道可分别产出正式版和测试版。 */
export const buildFlavorConfig = [
  { name: '正式版', value: 'production' },
  { name: '测试版', value: 'test' },
]

export const platformConfig = [
  { name: 'macOS', value: 'mac' },
  { name: 'Windows', value: 'win' },
]

export const DEFAULT_CHANNEL = 'china_cybercafe'
export const DEFAULT_REGION = 'cn'
export const DEFAULT_BUILD_FLAVOR = 'production'
export const DEFAULT_FIRMWARE_VERSION_POLICY = 'newer-only'

/**
 * 按区域划分的 CDN / 下载源（单一事实来源）。
 * - asset：网页静态资源、字体、manifest CDN
 * - firmware：固件升级包
 * - update：Electron autoUpdater / releaseNotes 镜像（国内历史为 http）
 */
export const regionCdn = {
  cn: {
    asset: 'https://hid.rapoo.cn',
    firmware: 'https://soft.rapoo.com/RapooDriver/FirmwareTest',
    update: 'http://hid.rapoo.cn',
  },
  global: {
    asset: 'https://hwsoft.rapoo.com/webdriver',
    firmware: 'https://hwsoft.rapoo.com/RapooDriver/Firmware',
    update: 'https://hwsoft.rapoo.com/webdriver',
  },
}

/** 网页版构建产物在 CDN 上的挂载路径（相对 asset）。 */
export const WEB_DIST_PATH = '/AHUB_WEB/dist/'

/** 由 channelConfig.region === 'global' 派生，避免再维护黑名单。 */
export const OVERSEAS_CHANNELS = channelConfig
  .filter((c) => c.region === 'global')
  .map((c) => c.value)

/**
 * @param {string | undefined | null} channel
 * @returns {'cn' | 'global'}
 */
export function getChannelRegion(channel) {
  const found = channelConfig.find((c) => c.value === channel)
  return found?.region ?? DEFAULT_REGION
}

/**
 * 固件版本资格策略。未知构建类型必须安全回落到仅允许升级。
 * @param {string | undefined | null} buildFlavor
 * @returns {'newer-only' | 'any-version'}
 */
export function getFirmwareVersionPolicy(buildFlavor = DEFAULT_BUILD_FLAVOR) {
  return buildFlavor === 'test' ? 'any-version' : DEFAULT_FIRMWARE_VERSION_POLICY
}

/**
 * Electron 更新目录标识：正式版保持历史目录，测试版追加 `_test` 防止串包。
 * @param {string | undefined | null} channel
 * @param {string | undefined | null} buildFlavor
 */
export function getDesktopUpdateChannel(
  channel = DEFAULT_CHANNEL,
  buildFlavor = DEFAULT_BUILD_FLAVOR,
) {
  const safeChannel = channelConfig.some((item) => item.value === channel)
    ? channel
    : DEFAULT_CHANNEL
  return buildFlavor === 'test' ? `${safeChannel}_test` : safeChannel
}

/**
 * @param {string | undefined | null} channel
 */
export function isOverseasChannel(channel) {
  return getChannelRegion(channel) === 'global'
}

/**
 * @param {string} [channel]
 */
export function getAssetBaseUrl(channel = DEFAULT_CHANNEL) {
  return regionCdn[getChannelRegion(channel)].asset
}

/**
 * @param {string} [channel]
 */
export function getFirmwareBaseUrl(channel = DEFAULT_CHANNEL) {
  return regionCdn[getChannelRegion(channel)].firmware
}

/**
 * Electron 桌面更新 feed / releaseImg 根地址。
 * @param {string} [channel]
 */
export function getUpdateBaseUrl(channel = DEFAULT_CHANNEL) {
  return regionCdn[getChannelRegion(channel)].update
}

/**
 * 网页版 Vite `base`（含尾部斜杠）。
 * @param {string} [channel]
 */
export function getWebDistBaseUrl(channel = DEFAULT_CHANNEL) {
  return `${getAssetBaseUrl(channel)}${WEB_DIST_PATH}`
}

/** 所有区域的 asset host，供构建期 host 替换插件做双向改写。 */
export function getAllAssetBaseUrls() {
  return [...new Set(Object.values(regionCdn).map((r) => r.asset))]
}
